import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';

import {
    init,
    reactRouterV6BrowserTracingIntegration,
    replayIntegration,
    setContext,
    setTag,
    withProfiler as withSentryProfiler,
    withSentryReactRouterV6Routing,
} from '@sentry/react';
import LogRocket from 'logrocket';

import { isGloballyHandledError } from '../../api/request';

const getConfig = (env: string, dsn: string) => ({
    dsn,
    environment: env,
    release: import.meta.env.VITE_APP_VERSION,
    dist: import.meta.env.VITE_GIT_COMMIT_HASH || '',

    debug: false,
    enabled: !['local', undefined].includes(env),

    integrations: [
        reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            block: ['input[type="password"]', 'input[type="email"]'],
        }),
    ],

    // Set tracesSampleRate to 0.1 to capture 10%
    // of transactions for performance monitoring.
    tracesSampleRate: 0.1,

    tracePropagationTargets: ['*.make.services', '*.ihme.services'],

    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend: (event, hint) => {
        if (hint?.originalException) {
            const error = hint.originalException;

            if (error?.isAxiosError && error?.response?.status === 401) {
                // Don't report to Sentry
                return null;
            }

            // Already reported (or deliberately skipped) by the global API error handler
            if (isGloballyHandledError(error)) {
                return null;
            }
        }

        // Check if it is an exception, and if so, show the report dialog
        /*if (env === 'dev' && event.exception && event.event_id) {
            showReportDialog({ eventId: event.event_id });
        }*/

        return event;
    },
});

export const initializeSentry = (env: string, dsn: string) => {
    const config = getConfig(env, dsn);
    init(config);

    // Optionally, capture errors from LogRocket
    if (window.LogRocket) {
        setTag('source', 'LogRocket');
        LogRocket.getSessionURL((sessionURL) => {
            setContext('session', { url: sessionURL });
        });
    }
};

export const withProfiler = withSentryProfiler;

export const withSentryReactRouterRouting = withSentryReactRouterV6Routing;

export {
    captureException,
    captureMessage,
    showReportDialog as showSentryReportDialog,
    withScope,
    withSentryRouting,
} from '@sentry/react';
